/**
 * One-shot outbox recovery after unlock (FR-MSG-001).
 *
 * Outbound rows still "queued" with no wire-outbox entry were never sealed for
 * the network: either left over from protocol v1 (their v1 ciphertext is dead)
 * or the app died between the local commit and sealing. Re-send them through
 * the v2 pipeline with their original id and timestamp.
 */
import type { Contact, Conversation, GroupRecord, MessageEvent } from "@veto/domain";
import { CONTENT_TYPE_GROUP, CONTENT_TYPE_TEXT } from "@veto/protocol";
import { getGroupByConversation, listOutboxForMessage, listQueuedOutbox } from "@veto/storage";
import { fanoutGroupMessage } from "./groupSend";
import type { Messenger } from "./messenger";

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function wireBody(m: MessageEvent): string {
  const timer = m.disappearTimerSec ? `\u0002${m.disappearTimerSec}\u0002` : "";
  return m.replyToEventId ? `\u0001${m.replyToEventId}\u0001${timer}${m.body}` : `${timer}${m.body}`;
}

export async function recoverOrphanedOutbound(opts: {
  messenger: Messenger;
  contacts: Contact[];
  conversations: Conversation[];
  /** Decrypting view-layer reader. */
  listMessages: (conversationId: string) => Promise<MessageEvent[]>;
  onStatus: (eventId: string, status: MessageEvent["status"], transport?: MessageEvent["transport"]) => Promise<void>;
}): Promise<number> {
  const queued = (await listQueuedOutbox()).filter(
    (m) =>
      m.status === "queued" &&
      Date.now() - m.createdAt < MAX_AGE_MS &&
      (m.contentType === CONTENT_TYPE_TEXT || m.contentType === CONTENT_TYPE_GROUP),
  );
  let resent = 0;
  const byConv = new Map<string, MessageEvent[]>();
  for (const m of queued) {
    if ((await listOutboxForMessage(m.eventId)).length > 0) continue;
    byConv.set(m.conversationId, [...(byConv.get(m.conversationId) ?? []), m]);
  }
  for (const [convId, rows] of byConv) {
    const conv = opts.conversations.find((c) => c.id === convId);
    if (!conv) continue;
    const decrypted = await opts.listMessages(convId);
    let group: GroupRecord | undefined;
    if (conv.type === "group") group = await getGroupByConversation(convId);
    const contact = conv.contactId ? opts.contacts.find((c) => c.id === conv.contactId) : undefined;
    for (const row of rows) {
      const m = decrypted.find((x) => x.eventId === row.eventId);
      if (!m || m.body.startsWith("enc:v1:") || m.body.startsWith("[unable")) continue;
      try {
        const out =
          conv.type === "group" && group
            ? await fanoutGroupMessage({
                group,
                plaintext: m.body,
                contacts: opts.contacts,
                messenger: opts.messenger,
                eventId: m.eventId,
                createdAt: m.createdAt,
                replyToEventId: m.replyToEventId,
              })
            : contact && !contact.blockedAt && contact.requestState !== "pending"
              ? await opts.messenger.send({
                  conversationId: convId,
                  recipients: [contact],
                  contentType: CONTENT_TYPE_TEXT,
                  body: wireBody(m),
                  eventId: m.eventId,
                  createdAt: m.createdAt,
                  tracksMessage: true,
                })
              : null;
        if (out) {
          await opts.onStatus(m.eventId, out.status, out.transport);
          resent += 1;
        }
      } catch {
        /* leave queued; next unlock retries */
      }
    }
  }
  return resent;
}
